// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * Package tier definitions and module-to-package mappings.
 * Sprint 18.0.0 — Package Deployment, License Management & Upgrade/Downgrade
 */
import { ModuleId as M } from '../types/module-config.js';
export const PackageTier = {
    LIGHT: 'light',
    STANDARD: 'standard',
    ENTERPRISE_UNLIMITED: 'enterprise_unlimited',
};
/**
 * Modules included in the Light package (11 configurable; Dashboard & Platform Settings are
 * always-on and not listed here — total user-visible count is 13).
 */
const LIGHT_MODULES = [
    M.CONSENT,
    M.DATA_MAPPING,
    M.RIGHTS_REQUESTS,
    M.ASSESSMENTS,
    M.AUDIT_TRAIL,
    M.INCIDENTS,
    M.PRIVACY_SCANNER,
    M.COMPLIANCE_FORMS,
    M.LOCALIZATION,
    M.ONBOARDING,
    M.HELP_CENTER,
    M.DATA_SUBJECTS,
];
/** Modules included in the Standard package (Light + 11 = 22 configurable; 24 total) */
const STANDARD_MODULES = [
    ...LIGHT_MODULES,
    M.CLASSIFICATION,
    M.CONSENT_PROPERTIES,
    M.DISCOVERY,
    M.GOVERNANCE,
    M.VENDOR_RISK,
    M.CONNECTORS,
    M.DELETION_ORCHESTRATION,
    M.INCIDENT_OPS,
    M.MONITORING,
    M.PROGRAM_GOVERNANCE,
    M.TASK_MANAGEMENT,
    // Note: ACCESS_ACCOUNTABILITY, ORGANIZATIONS, PARTNER_WORKSPACE, SECTOR_OVERLAYS are Enterprise Unlimited only
];
/**
 * Modules included in the Enterprise Unlimited package.
 *
 * Sprint 63.42.0 (owner ruling OR-26) — Sector Overlays is the fourth
 * Enterprise-only module: optional sector overlays, their installer and the
 * registers they activate are not available on Light or Standard, and the
 * server refuses them there (`requireModuleEntitlement`).
 */
const ENTERPRISE_MODULES = [
    ...STANDARD_MODULES,
    M.ACCESS_ACCOUNTABILITY,
    M.ORGANIZATIONS,
    M.PARTNER_WORKSPACE,
    M.SECTOR_OVERLAYS,
];
export const PACKAGE_DEFINITIONS = {
    [PackageTier.LIGHT]: {
        tier: PackageTier.LIGHT,
        name_key: 'packages.light.name',
        description_key: 'packages.light.description',
        included_modules: LIGHT_MODULES,
        max_power_users: 10,
        features: {
            multi_org: false,
            partner_workspace: false,
            enhanced_support_eligible: true,
            co_creation_program: false,
            formal_sla: false,
            onboarding_pack: false,
            quarterly_review: false,
            // Sprint 47.4.0 — add-on offered across all three tiers
            immutable_audit_trails: true,
        },
    },
    [PackageTier.STANDARD]: {
        tier: PackageTier.STANDARD,
        name_key: 'packages.standard.name',
        description_key: 'packages.standard.description',
        included_modules: STANDARD_MODULES,
        max_power_users: null,
        features: {
            multi_org: false,
            partner_workspace: false,
            enhanced_support_eligible: true,
            co_creation_program: false,
            formal_sla: false,
            onboarding_pack: false,
            quarterly_review: false,
            // Sprint 47.4.0 — add-on offered across all three tiers
            immutable_audit_trails: true,
        },
    },
    [PackageTier.ENTERPRISE_UNLIMITED]: {
        tier: PackageTier.ENTERPRISE_UNLIMITED,
        name_key: 'packages.enterprise_unlimited.name',
        description_key: 'packages.enterprise_unlimited.description',
        included_modules: ENTERPRISE_MODULES,
        max_power_users: null,
        features: {
            multi_org: true,
            partner_workspace: true,
            enhanced_support_eligible: true,
            co_creation_program: true,
            formal_sla: true,
            onboarding_pack: true,
            quarterly_review: true,
            // Sprint 47.4.0 — add-on offered across all three tiers
            immutable_audit_trails: true,
        },
    },
};
/** Get the modules included in a package tier */
export function getPackageModules(tier) {
    return PACKAGE_DEFINITIONS[tier].included_modules;
}
/** Check if a module is available in a package tier */
export function isModuleInPackage(moduleId, tier) {
    return PACKAGE_DEFINITIONS[tier].included_modules.includes(moduleId);
}
/* ──────────────────────────────────────────────────────────────────
 * Sprint 45.3.0 — Help Center package-tier awareness
 *
 * Help articles carry a `module` field that uses hyphenated codes
 * (e.g. `vendor-risk`, `data-mapping`), while `ModuleId` constants
 * use underscores (e.g. `vendor_risk`). The map below is the single
 * source of truth for converting between the two conventions.
 *
 * When a new help article adds a module code, update this map. The
 * visibility check fails OPEN on unknown codes so forgetting to
 * update the map never silently hides articles.
 * ────────────────────────────────────────────────────────────────── */
/**
 * Maps hyphenated help-article `module` codes → underscored `ModuleId`
 * constants. A `null` value means "this help-module is a pseudo-module
 * that doesn't correspond to a real ModuleId" — visibility for those
 * is determined by the ALWAYS_VISIBLE / ENTERPRISE_ONLY sets below.
 */
export const HELP_MODULE_TO_MODULE_ID = {
    consent: M.CONSENT,
    'data-mapping': M.DATA_MAPPING,
    'rights-requests': M.RIGHTS_REQUESTS,
    assessments: M.ASSESSMENTS,
    'compliance-forms': M.COMPLIANCE_FORMS,
    incidents: M.INCIDENTS,
    'incident-ops': M.INCIDENT_OPS,
    'vendor-risk': M.VENDOR_RISK,
    classification: M.CLASSIFICATION,
    discovery: M.DISCOVERY,
    'deletion-orchestration': M.DELETION_ORCHESTRATION,
    governance: M.GOVERNANCE,
    'program-governance': M.PROGRAM_GOVERNANCE,
    monitoring: M.MONITORING,
    'access-accountability': M.ACCESS_ACCOUNTABILITY,
    connectors: M.CONNECTORS,
    organizations: M.ORGANIZATIONS,
    'task-management': M.TASK_MANAGEMENT,
    'partner-marketplace': M.PARTNER_WORKSPACE,
    localization: M.LOCALIZATION,
    // Sprint 63.42.0 (OR-26) — Module 30; its help articles render on Enterprise only.
    'sector-overlays': M.SECTOR_OVERLAYS,
    'consent-properties': M.CONSENT_PROPERTIES,
    'audit-trail': M.AUDIT_TRAIL,
    'privacy-scanner': M.PRIVACY_SCANNER,
    'data-subjects': M.DATA_SUBJECTS,
    onboarding: M.ONBOARDING,
    'help-center': M.HELP_CENTER,
    // Pseudo-modules — not gated via `isModuleInPackage`; see below.
    dashboard: null,
    'platform-settings': null,
};
/**
 * Pseudo-module codes that are always visible regardless of tier.
 * `dashboard` articles describe the landing page every tier shares.
 */
export const ALWAYS_VISIBLE_HELP_MODULES = new Set(['dashboard']);
/**
 * Pseudo-module codes that are gated to Enterprise Unlimited.
 * `platform-settings` covers Files Configuration, RBAC admin,
 * custom role templates, SSO, and audit-config surfaces — all
 * Enterprise-tier per the 44.3.0 Permissions & Roles Lock-In.
 */
export const ENTERPRISE_ONLY_HELP_MODULES = new Set(['platform-settings']);
/**
 * Resolve whether a help article's `module` field should render for
 * the given package tier. Encapsulates four rules:
 *
 *   1. `null` / `undefined` / empty → cross-cutting, always visible.
 *   2. In `ALWAYS_VISIBLE_HELP_MODULES` → always visible.
 *   3. In `ENTERPRISE_ONLY_HELP_MODULES` → visible only on
 *      `enterprise_unlimited`.
 *   4. Mapped to a real `ModuleId` → delegate to `isModuleInPackage`.
 *
 * Fail-open on unknown codes: if the mapper doesn't recognise
 * `helpModule`, the article renders. This protects against silent
 * disappearance when the authoring pipeline outpaces mapper updates.
 */
export function isHelpModuleVisibleInTier(helpModule, tier) {
    if (!helpModule)
        return true;
    // Fail-open also on unknown tier codes — a runtime tier value outside the
    // canonical three (license-payload typo, future custom tier, downcast from
    // string) must not crash help-article rendering or silently hide content.
    // The fail-open principle that protects against unmapped help-module codes
    // extends to unmapped tiers; defaulting to "show everything" is safer than
    // an empty help center on a tier we don't recognise.
    if (!PACKAGE_DEFINITIONS[tier])
        return true;
    if (ALWAYS_VISIBLE_HELP_MODULES.has(helpModule))
        return true;
    if (ENTERPRISE_ONLY_HELP_MODULES.has(helpModule)) {
        return tier === PackageTier.ENTERPRISE_UNLIMITED;
    }
    const moduleId = HELP_MODULE_TO_MODULE_ID[helpModule];
    // Unknown or null-mapped (but not in the special sets above) — fail open.
    if (moduleId === undefined || moduleId === null)
        return true;
    return isModuleInPackage(moduleId, tier);
}
//# sourceMappingURL=packages.js.map